import { createSlice } from "@reduxjs/toolkit";
import {
  getReviewWidgetConfigAction,
  getReviewWidgetListAction,
  getReviewWidgetDetailAction,
  getReviewWidgetJsonldAction,
  addReviewWidgetAction,
  editReviewWidgetAction,
  regenerateReviewSummaryAction,
} from "../actions/ReviewWidgetActions";

interface ReviewWidgetState {
  config: {
    data: any;
    loading: boolean;
    error: any;
  };
  list: {
    data: any[];
    nextCursor: string | null;
    loading: boolean;
    error: any;
  };
  detail: {
    data: any;
    loading: boolean;
    error: any;
  };
  jsonld: {
    data: any;
    loading: boolean;
    error: any;
  };
  isSaving: boolean;
  isAdding: boolean;
  isRegeneratingSummary: boolean;
}

const initialState: ReviewWidgetState = {
  config: {
    data: null,
    loading: false,
    error: null,
  },
  list: {
    data: [],
    nextCursor: null,
    loading: false,
    error: null,
  },
  detail: {
    data: null,
    loading: false,
    error: null,
  },
  jsonld: {
    data: null,
    loading: false,
    error: null,
  },
  isSaving: false,
  isAdding: false,
  isRegeneratingSummary: false,
};

const reviewWidgetSlice = createSlice({
  name: "reviewWidget",
  initialState,
  reducers: {
    resetReviewWidgetState: (state) => {
      state.config = initialState.config;
      state.list = initialState.list;
      state.detail = initialState.detail;
      state.jsonld = initialState.jsonld;
    },
  },
  extraReducers: (builder) => {
    // Config
    builder
      .addCase(getReviewWidgetConfigAction.pending, (state) => {
        state.config.loading = true;
        state.config.error = null;
      })
      .addCase(getReviewWidgetConfigAction.fulfilled, (state, action) => {
        state.config.loading = false;
        state.config.data = action.payload?.data || action.payload;
      })
      .addCase(getReviewWidgetConfigAction.rejected, (state, action) => {
        state.config.loading = false;
        state.config.error = action.payload;
      });

    // List
    builder
      .addCase(getReviewWidgetListAction.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(getReviewWidgetListAction.fulfilled, (state, action) => {
        state.list.loading = false;
        const incomingData = action.payload?.data || [];
        const isPagination = Boolean(action.meta?.arg?.cursor);
        state.list.data = isPagination
          ? [...state.list.data, ...incomingData]
          : incomingData;
        state.list.nextCursor = action.payload?.nextCursor ?? null;
      })
      .addCase(getReviewWidgetListAction.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload;
      });

    // Detail
    builder
      .addCase(getReviewWidgetDetailAction.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(getReviewWidgetDetailAction.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data = action.payload?.data || action.payload;
      })
      .addCase(getReviewWidgetDetailAction.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload;
      });

    // JsonLD
    builder
      .addCase(getReviewWidgetJsonldAction.pending, (state) => {
        state.jsonld.loading = true;
        state.jsonld.error = null;
      })
      .addCase(getReviewWidgetJsonldAction.fulfilled, (state, action) => {
        state.jsonld.loading = false;
        state.jsonld.data = action.payload?.data || action.payload;
      })
      .addCase(getReviewWidgetJsonldAction.rejected, (state, action) => {
        state.jsonld.loading = false;
        state.jsonld.error = action.payload;
      });

    // Add
    builder
      .addCase(addReviewWidgetAction.pending, (state) => {
        state.isAdding = true;
      })
      .addCase(addReviewWidgetAction.fulfilled, (state) => {
        state.isAdding = false;
      })
      .addCase(addReviewWidgetAction.rejected, (state) => {
        state.isAdding = false;
      });

    // Edit
    builder
      .addCase(editReviewWidgetAction.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(editReviewWidgetAction.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(editReviewWidgetAction.rejected, (state) => {
        state.isSaving = false;
      });

    // Regenerate summary
    builder
      .addCase(regenerateReviewSummaryAction.pending, (state) => {
        state.isRegeneratingSummary = true;
      })
      .addCase(regenerateReviewSummaryAction.fulfilled, (state, action) => {
        state.isRegeneratingSummary = false;
        if (state.config.data && action.payload) {
          const resData = (action.payload as any)?.data || action.payload;
          const summary =
            resData?.ai_summary ||
            resData?.summary ||
            resData?.review_summary?.narrative ||
            resData?.narrative ||
            (typeof resData === "string" ? resData : null);
          if (summary) {
            state.config.data.ai_summary = summary;
          }
        }
      })
      .addCase(regenerateReviewSummaryAction.rejected, (state) => {
        state.isRegeneratingSummary = false;
      });
  },
});

export const { resetReviewWidgetState } = reviewWidgetSlice.actions;
export default reviewWidgetSlice.reducer;
